'use client';

import { useSubmitFeedback } from '@shared/api-utils';
import { useSession } from '@shared/auth';
import { Button, Textarea } from '@shared/components/shadcn';
import { LottolandSmileLogo } from '@shared/icons';
import { useLocale, useTranslations } from 'next-intl';
import { useEffect, useState, type ComponentProps } from 'react';
import { buildFeedbackContext } from '../feedback/utils/feedbackContext';
import { FeedbackError } from '../FeedbackError/FeedbackError';
import { FeedbackSuccess } from '../FeedbackSuccess/FeedbackSuccess';
import { FeedbackSheet } from '../global-sheets/feedback/FeedbackSheet/FeedbackSheet';
import { IconBadge } from '../IconBadge/IconBadge';
import { Rating } from '../Rating/Rating';
import { toast } from '../Toast/Toast';

type FeedbackRatingProps = {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
} & ComponentProps<'div'>;

const FEEDBACK_RATING_STEPS = {
  RATING: 'RATING',
  SUBMITTING: 'SUBMITTING',
  SUCCESS: 'SUCCESS',
  ERROR: 'ERROR',
} as const;
type FeedbackRatingStep = 'RATING' | 'SUBMITTING' | 'SUCCESS' | 'ERROR';

const FeedbackRating = ({ open = true, onOpenChange, ...props }: FeedbackRatingProps) => {
  const { session } = useSession();
  const t = useTranslations('global_sheets.feedback_rating');
  const locale = useLocale();
  const playerId = session?.profile?.id;
  const submitFeedback = useSubmitFeedback();
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [step, setStep] = useState<FeedbackRatingStep>(FEEDBACK_RATING_STEPS.RATING);
  const isSubmitting = step === FEEDBACK_RATING_STEPS.SUBMITTING;

  useEffect(() => {
    if (open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setRating(0);
      setComment('');
    }
  }, [open]);

  useEffect(() => {
    if (step === FEEDBACK_RATING_STEPS.SUCCESS) {
      toast('Feedback sent, thank you!', { type: 'success', position: 'bottom-center' });
      onOpenChange?.(false);
    }
  }, [step, onOpenChange]);

  const handleRatingChange = (newRating: number) => {
    setRating(newRating);
  };

  const handleCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setComment(e.target.value);
  };

  const handleSubmit = async () => {
    if (rating === 0) return;
    setStep(FEEDBACK_RATING_STEPS.SUBMITTING);

    const requestBody = {
      pid: playerId || '',
      comment,
      star_rating: rating,
      context: buildFeedbackContext(locale),
    };

    try {
      // STG URL -> `https://cs-hub-uk-cs-hub-uk-update-gnc4aqa5eba6ckb5.westeurope-01.azurewebsites.net${API_ROUTES.FEEDBACK}`
      await submitFeedback.mutateAsync(requestBody);

      setStep(FEEDBACK_RATING_STEPS.SUCCESS);
    } catch {
      setStep(FEEDBACK_RATING_STEPS.ERROR);
    }
  };

  const getTrackContainer = () => {
    if (step === FEEDBACK_RATING_STEPS.RATING) return 'Rating';
    if (step === FEEDBACK_RATING_STEPS.ERROR) return 'Feedback Failure';
    return '';
  };

  return (
    <FeedbackSheet
      open={open}
      onOpenChange={onOpenChange}
      dataTestIdBaseName="feedback-rating"
      title={t('title')}
      data-track-feature="Feedback"
      data-track-container={getTrackContainer()}
    >
      {step === FEEDBACK_RATING_STEPS.RATING && (
        <div className="flex flex-col gap-5 px-6 py-10 h-full md:h-auto" {...props}>
          <IconBadge icon={LottolandSmileLogo} shapeType="square" />
          <div className="flex flex-col gap-10 flex-grow">
            <div>
              <p className="heading-4 pb-2">{t('title')}</p>
              <p>{t('subtitle')}</p>
            </div>
            <div className="flex justify-center items-center">
              <Rating value={rating} onRatingChange={handleRatingChange} />
            </div>
            <div className="flex flex-col gap-2 pb-5">
              <p id="feedback-comments-label" className="paragraph-explanatory">
                {t('comments_label')}
              </p>
              <Textarea
                value={comment}
                onChange={handleCommentChange}
                className="min-h-[120px] resize-none border border-border"
                maxLength={200}
                data-testid="feedback-rating-comment-textarea"
                data-track-name="Comments"
                data-track-privacy="Mask"
                disabled={rating === 0 || isSubmitting}
                aria-labelledby="feedback-comments-label"
              />
            </div>
          </div>
          <div data-track-container="Actions">
            <Button onClick={handleSubmit} disabled={rating === 0 || isSubmitting} data-testid="feedback-rating-submit-button" data-track-name="Submit" data-track-rating={rating}>
              {t('submit_button')}
            </Button>
          </div>
        </div>
      )}

      {step === FEEDBACK_RATING_STEPS.SUBMITTING && <FeedbackSuccess primaryText={t('success.primary')} />}

      {step === FEEDBACK_RATING_STEPS.ERROR && (
        <FeedbackError primaryText={t('error.primary')} buttonText={t('error.retry')} onRetry={() => setStep(FEEDBACK_RATING_STEPS.RATING)} />
      )}
    </FeedbackSheet>
  );
};

FeedbackRating.displayName = 'FeedbackRating';

export default FeedbackRating;
export type { FeedbackRatingProps };
